import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { DECK_CHECK_FIELDS } from './deck-checks';
import type {
  DeckCheck,
  Nullable,
  TournamentPlayerStatus,
  UserWithToken,
  WithData,
} from './api.types';

export type DecklistSubmissionKind = 'YDK' | 'IMAGE' | 'PDF';

/** Revisión del staff de una lista entregada. */
export type DecklistReviewStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type DecklistSubmission = {
  tournamentPlayerId: number;
  userId: number;
  name: string;
  cossyId: string | null;
  discordId: string | null;
  status: TournamentPlayerStatus;
  /** El nombre del mazo del inscripto (o el de su meta deck). */
  deckName: string | null;
  /** La decklist entregada (`Decklists.getDecklist`). null = no entregó. */
  decklistId: number | null;
  /** ISO. null = no entregó. */
  submittedAt: string | null;
  /** Entregó con el torneo empezado: corresponde sanción. */
  late: boolean;
  submissionCount: number;
  kind: DecklistSubmissionKind | null;
  mainCount: number | null;
  extraCount: number | null;
  sideCount: number | null;
  /** null = sin entrega, o entregada antes de que existiera la revisión. */
  reviewStatus: DecklistReviewStatus | null;
  /** ISO. */
  reviewedAt: string | null;
  /** Nombre completo de quien la revisó. */
  reviewedBy: string | null;
  /** Lo ve el jugador. */
  rejectionReason: string | null;
  /** Rechazada con permiso de reenviar aunque la entrega esté pausada. */
  resubmitAllowed: boolean;
  /** Los deck checks del jugador en el torneo, el más nuevo primero. */
  deckChecks: DeckCheck[];
};

export type DecklistSubmissionFile = {
  filename: string;
  contentType: string;
  base64: string;
};

/** Entregas de lista por Discord de un torneo (Super YACS). Admin. */
export const getDecklistSubmissions = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmission[]>>(
    gql`
      ${DECK_CHECK_FIELDS}
      query decklistSubmissions($tournamentId: Int!) {
        data: decklistSubmissions(tournamentId: $tournamentId) {
          tournamentPlayerId
          userId
          name
          cossyId
          discordId
          status
          deckName
          decklistId
          submittedAt
          late
          submissionCount
          kind
          mainCount
          extraCount
          sideCount
          reviewStatus
          reviewedAt
          reviewedBy
          rejectionReason
          resubmitAllowed
          deckChecks {
            ...DeckCheckFields
          }
        }
      }
    `,
    { tournamentId }
  );

/** El `.ydk`, la foto o el PDF entregados, en base64 (están en S3 privado). Staff del torneo. */
export const getDecklistSubmissionFile = async (
  user: Nullable<UserWithToken>,
  tournamentPlayerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmissionFile | null>>(
    gql`
      query decklistSubmissionFile($tournamentPlayerId: Int!) {
        data: decklistSubmissionFile(tournamentPlayerId: $tournamentPlayerId) {
          filename
          contentType
          base64
        }
      }
    `,
    { tournamentPlayerId }
  );

export type DecklistSubmissionUpload = {
  ok: boolean;
  /** Por qué se rechazó: `invalid_ydk`, `bad_format`, `too_big`, `not_found`… */
  reason: string | null;
  errors: string[];
  warnings: string[];
  /** main/extra/side leídos, si los hay. */
  counts: string | null;
};

/**
 * Un juez sube la lista de un jugador (Super YACS). `base64` acepta el prefijo
 * `data:<mime>;base64,` de `readAsDataURL`. Admin.
 */
export const uploadDecklistSubmission = async (
  user: Nullable<UserWithToken>,
  input: {
    tournamentPlayerId: number;
    filename: string;
    contentType?: string;
    base64: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmissionUpload>>(
    gql`
      mutation uploadDecklistSubmission($input: UploadDecklistSubmissionInput!) {
        data: uploadDecklistSubmission(input: $input) {
          ok
          reason
          errors
          warnings
          counts
        }
      }
    `,
    { input }
  );

/** Si el botón de #entrega-de-decklist acepta listas en ese torneo. Admin. */
export const getDecklistSubmissionsOpen = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      query decklistSubmissionsOpen($tournamentId: Int!) {
        data: decklistSubmissionsOpen(tournamentId: $tournamentId)
      }
    `,
    { tournamentId }
  );

/**
 * Abre o cierra la entrega por Discord: cerrada no entra ninguna lista, ni la
 * primera ni un reemplazo. La subida de un juez sigue andando. Admin.
 */
export const setDecklistSubmissionsOpen = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  open: boolean
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation setDecklistSubmissionsOpen($tournamentId: Int!, $open: Boolean!) {
        data: setDecklistSubmissionsOpen(tournamentId: $tournamentId, open: $open)
      }
    `,
    { tournamentId, open }
  );

/** El `.ydk` ordenado de la entrega; null si no hay cartas. Staff del torneo. */
export const getDecklistSubmissionYdk = async (
  user: Nullable<UserWithToken>,
  tournamentPlayerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmissionFile | null>>(
    gql`
      query decklistSubmissionYdk($tournamentPlayerId: Int!) {
        data: decklistSubmissionYdk(tournamentPlayerId: $tournamentPlayerId) {
          filename
          contentType
          base64
        }
      }
    `,
    { tournamentPlayerId }
  );

/**
 * Aprueba o rechaza la última entrega. Rechazada, `allowResubmit` deja reenviar
 * aunque la entrega esté pausada. `submissionCount` es la entrega que el staff
 * tenía en pantalla: si el jugador ya subió otra, el API contesta `stale`.
 * Staff del torneo.
 */
export const reviewDecklistSubmission = async (
  user: Nullable<UserWithToken>,
  input: {
    tournamentPlayerId: number;
    status: DecklistReviewStatus;
    rejectionReason?: string | null;
    allowResubmit?: boolean;
    submissionCount?: number;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmission>>(
    gql`
      ${DECK_CHECK_FIELDS}
      mutation reviewDecklistSubmission(
        $tournamentPlayerId: Int!
        $status: DecklistReviewStatus!
        $rejectionReason: String
        $allowResubmit: Boolean
        $submissionCount: Int
      ) {
        data: reviewDecklistSubmission(
          tournamentPlayerId: $tournamentPlayerId
          status: $status
          rejectionReason: $rejectionReason
          allowResubmit: $allowResubmit
          submissionCount: $submissionCount
        ) {
          tournamentPlayerId
          userId
          name
          cossyId
          discordId
          status
          deckName
          decklistId
          submittedAt
          late
          submissionCount
          kind
          mainCount
          extraCount
          sideCount
          reviewStatus
          reviewedAt
          reviewedBy
          rejectionReason
          resubmitAllowed
          deckChecks {
            ...DeckCheckFields
          }
        }
      }
    `,
    input
  );

export type DecklistBlockedReason = 'disabled' | 'closed' | 'paused';

/** Lo que ve el jugador de su propia entrega. Sin deck checks. */
export type MyTournamentDecklist = {
  /** El torneo pide lista. */
  required: boolean;
  /** La entrega está abierta para todos (torneo abierto o en curso, sin pausar). */
  open: boolean;
  /** Puede entregar ahora: incluye el reenvío habilitado tras un rechazo. */
  canSubmit: boolean;
  /**
   * Por qué no puede entregar: el torneo no pide lista, terminó o los jueces
   * la pausaron. null si puede.
   */
  blockedReason: DecklistBlockedReason | null;
  reviewStatus: DecklistReviewStatus | null;
  rejectionReason: string | null;
  /** ISO. null = no entregó. */
  submittedAt: string | null;
  submissionCount: number;
  kind: DecklistSubmissionKind | null;
  mainCount: number | null;
  extraCount: number | null;
  sideCount: number | null;
  /** Una foto o un PDF que todavía se está leyendo. */
  readPending: boolean;
};

/** La entrega propia en ese torneo. null si no está inscripto, o si el torneo no pide lista y nunca entregó. */
export const getMyTournamentDecklist = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<MyTournamentDecklist | null>>(
    gql`
      query myTournamentDecklist($tournamentId: Int!) {
        data: myTournamentDecklist(tournamentId: $tournamentId) {
          required
          open
          canSubmit
          blockedReason
          reviewStatus
          rejectionReason
          submittedAt
          submissionCount
          kind
          mainCount
          extraCount
          sideCount
          readPending
        }
      }
    `,
    { tournamentId }
  );

/** El archivo que entregó el propio jugador, en base64. */
export const getMyTournamentDecklistFile = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmissionFile | null>>(
    gql`
      query myTournamentDecklistFile($tournamentId: Int!) {
        data: myTournamentDecklistFile(tournamentId: $tournamentId) {
          filename
          contentType
          base64
        }
      }
    `,
    { tournamentId }
  );

/**
 * Las cartas leídas de la propia entrega como `.ydk` (también de una foto o un
 * PDF ya leídos). `null` si no entregó o todavía no hay cartas leídas.
 */
export const getMyTournamentDecklistYdk = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmissionFile | null>>(
    gql`
      query myTournamentDecklistYdk($tournamentId: Int!) {
        data: myTournamentDecklistYdk(tournamentId: $tournamentId) {
          filename
          contentType
          base64
        }
      }
    `,
    { tournamentId }
  );

/**
 * El jugador entrega su lista desde las apps. `base64` acepta el prefijo
 * `data:<mime>;base64,`; el techo es ~7 MB (`/graphql` sólo acepta JSON).
 */
export const submitTournamentDecklist = async (
  user: Nullable<UserWithToken>,
  input: {
    tournamentId: number;
    filename: string;
    contentType?: string;
    base64: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<DecklistSubmissionUpload>>(
    gql`
      mutation submitTournamentDecklist($input: SubmitTournamentDecklistInput!) {
        data: submitTournamentDecklist(input: $input) {
          ok
          reason
          errors
          warnings
          counts
        }
      }
    `,
    { input }
  );
